import styled, { css } from 'styled-components';
import trash from '~/assets/icons/trash.svg';
import { TextLabel } from '../TextLabel';

const StyledContainer = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 15;
	width: 100%;
	height: 100%;
	background-color: rgba(17, 19, 20, 0.98);
`;

const StyledHeadContainer = styled.div`
	display: grid;
	grid-template-columns: 20% 60% 20%;
	padding: 20px;
	align-items: center;
`;

const StyledBackButton = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	height: 40px;
	justify-self: start;
	padding: 0 10px;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 6px;
		height: 10px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${TextLabel} {
		font-weight: 400;
		font-size: 14px;
		color: #ffffff;
		transition: color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(100%);
		}

		${TextLabel} {
			color: #000000;
		}
	}
`;

const StyledCrossButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	justify-self: end;
	width: 40px;
	height: 40px;
	background-color: rgba(255, 255, 255, 0.1);
	border-radius: 10px;
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(100%);
		}
	}
`;

const StyledMainContainer = styled.div`
	position: relative;
	width: 430px;
	margin: 0 auto;
	padding: 10px 0 20px;
`;

const StyledActivePhotoContainer = styled.div`
	display: flex;
	justify-content: center;
`;

const StyledRemovePhoto = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 17px;
	height: 20px;
	opacity: 0;
	background-image: url(${trash});
	background-repeat: no-repeat;
	background-position: center;
	background-size: contain;
	transition: opacity 0.3s ease;
	cursor: pointer;
`;

const StyledPhoto = styled.div`
	position: relative;
	width: auto;
	height: 80px;
	border-radius: 10px;
	overflow: hidden;

	img {
		width: 100%;
		height: 100%;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	&:hover {
		${StyledRemovePhoto} {
			opacity: 1;
		}

		img {
			filter: brightness(0.25);
		}
	}
`;

const StyledPhotoList = styled.div`
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 20px;
	margin-top: 20px;
`;

const StyledAddPhoto = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 7px;
	width: auto;
	height: 80px;
	border-radius: 10px;
	background-color: rgba(255, 255, 255, 0.1);
	transition: background-color 0.3s ease;
	cursor: pointer;

	img {
		width: 14px;
		height: 14px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${TextLabel} {
		font-size: 12px;
		color: #ffffff;
		transition: color 0.3s ease;
	}

	&:hover {
		background-color: #ffffff;

		img {
			filter: invert(100%);
		}

		${TextLabel} {
			color: #000000;
		}
	}
`;

const StyledTextAreaContainer = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 20px;
	padding: 17px 20px;
	background-color: rgba(255, 255, 255, 0.05);
	border-radius: 10px;

	textarea {
		width: 100%;
		font-family: 'GT Walsheim Pro';
		font-style: normal;
		font-weight: 400;
		font-size: 14px;
		line-height: 16px;
		color: #ffffff;
		opacity: 0.5;
		background-color: transparent;
		border: none;
		resize: none;

		&:focus-visible {
			outline: none;
		}
	}

	.bottom {
		display: flex;
		align-items: center;
		justify-content: space-between;

		${TextLabel} {
			font-size: 12px;
			color: #ffffff;
			opacity: 0.25;
		}
	}
`;

const StyledPriceSettingsContainer = styled.div`
	display: grid;
	grid-template-columns: 200px 1fr;
	gap: 20px;
	margin-top: 20px;
`;

const StyledTimeContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;

	& > div > .value {
		border-radius: 10px 0 0 10px;
	}
`;

const StyledTimePriceContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	height: 40px;
	padding: 0 10px;
	border-radius: 0 10px 10px 0;
	background-color: rgba(255, 255, 255, 0.05);

	${TextLabel} {
		font-size: 14px;
		color: #ffffff;
	}
`;

const StyledPublishButton = styled.div<{ $disabled?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 20px;
	width: 100%;
	height: 60px;
	margin-top: 52px;
	border-radius: 10px;
	background-color: ${(props) =>
		props.$disabled ? 'rgba(255,255,255,0.05)' : '#1f8034'};
	transition: background-color 0.3s ease;

	img {
		width: 20px;
		height: 20px;
		user-select: none;
		pointer-events: none;
		transition: filter 0.3s ease;
	}

	${TextLabel} {
		font-size: 18px;
		color: ${(props) =>
			props.$disabled ? 'rgba(255,255,255,0.5)' : '#ffffff'};
		transition: color 0.3s ease;
	}

	${(props) =>
		!props.$disabled &&
		css`
			cursor: pointer;

			&:hover {
				background-color: #ffffff;

				img {
					filter: invert(100%);
				}

				${TextLabel} {
					color: #000000;
				}
			}
		`}
`;

export {
	StyledActivePhotoContainer,
	StyledAddPhoto,
	StyledBackButton,
	StyledContainer,
	StyledCrossButton,
	StyledHeadContainer,
	StyledMainContainer,
	StyledPhoto,
	StyledPhotoList,
	StyledPriceSettingsContainer,
	StyledPublishButton,
	StyledRemovePhoto,
	StyledTextAreaContainer,
	StyledTimeContainer,
	StyledTimePriceContainer,
};
